import { FC, PropsWithChildren, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Button, Grid, Typography, CircularProgress } from '@mui/material'
import {
  HasSidebarLayout,
  DataLoad,
  CollapsibleSection,
  EntityHeader,
  PropsInjector,
  TabGroupFilter,
  StatefulDialog,
  AssignSensorToContractor,
  MakeAvailableSensorModal,
  MarkLostSensorModal,
  SensorStagingLabel,
  SensorLostLabel
} from 'src/components'
import { useAssignGatewayRequest, useGatewayRequest, useGatewayStatesRequest } from 'src/hooks/api'
import { useDialog, useQRCode, useGatewayLabel, useGatewayStateUpdate } from 'src/hooks/ui'
import { Gateway, GatewayState } from 'src/api'
import { DialogNames, SearchParamsKeys, GatewayStateList } from 'src/enums'
import { capitalizeFirstLetter } from 'src/utils/formatters'
import { GatewayInfo } from 'src/components/sections/gateway/gatewayInfo'
import { GatewayCurrentProjects, GatewayPastProjects, GatewayLastReadings, GatewaySensors } from 'src/components/sections/gateway'

interface LoRaWanGatewayShowContentProps {
  gateway?: Gateway
  refetchGateway?: () => void
  gatewayStates?: GatewayState[]
}

interface GatewayStatusProps {
  gateway: Gateway
  gatewayStates: GatewayState[]
  updateState: (payload: { gatewayId: number; state: GatewayState; onSuccess?: () => void; onError?: () => void }) => void
  openDialog: () => void
  refetchGateway?: () => void
}

const GatewayStatus: FC<GatewayStatusProps> = ({ gateway, gatewayStates, updateState, openDialog, refetchGateway }) => {
  const getNextAction: { action: () => void } = useCallback(() => {
    const currentState: string = gateway?.state?.name || ''
    let _nextAction: () => void = () => {}
    switch (currentState) {
      case 'new':
        const nextState = gatewayStates.find((state) => state.name === 'available')
        _nextAction = () =>
          updateState({
            gatewayId: gateway.id,
            state: nextState as GatewayState,
            onSuccess: refetchGateway
          })
        break
      case 'available':
      case 'assigned':
        _nextAction = openDialog
        break
      default:
        break
    }
    return {
      action: _nextAction
    }
  }, [gateway, gatewayStates, updateState, openDialog, refetchGateway])()

  const updateStateButton = () => {
    const labelMap: Record<string, string> = {
      new: 'Make Ready to use',
      available: 'Assign',
      assigned: 'Reassign'
    }
    const actionLabel: string = labelMap[gateway?.state?.name || '']

    return actionLabel ? `(${actionLabel})` : ''
  }

  return (
    <Typography variant="h6">
      {capitalizeFirstLetter(gateway.state?.name || '')}{' '}
      {updateStateButton() ? (
        <Button onClick={getNextAction.action} variant="text">
          {updateStateButton()}
        </Button>
      ) : null}
    </Typography>
  )
}

const getStatusList = ({ gateway }: { gateway: Gateway }) => {
  return [
    {
      status: 'Current Projects',
      content: <GatewayCurrentProjects gateway={gateway} noResultText="Current gateway does not belong to any project" showFooter={false} />
    },
    {
      status: 'Past Projects',
      content: <GatewayPastProjects gateway={gateway} noResultText="Current gateway does not have past projects" showFooter={false} />
    }
  ]
}

const LoRaWanGatewayShowContent: FC<LoRaWanGatewayShowContentProps> = ({ gateway = null, refetchGateway, gatewayStates }) => {
  const { loading: loadingQRCode, generateCode } = useQRCode()
  const { generatingLabel, downloadLabel } = useGatewayLabel()
  const { openDialog: openAssignGatewayDialog, closeDialog: closeAssignGatewayDialog } = useDialog(DialogNames.AssignSensorToContractor)
  const assignGatewayRequest = useAssignGatewayRequest({
    options: {
      onSettled: () => closeAssignGatewayDialog(),
      onSuccess: refetchGateway
    }
  })
  const {
    openGatewayMakeAvailableDialog,
    closeGatewayMakeAvailableDialog,
    openGatewayMarkLostDialog,
    closeGatewayMarkLostDialog,
    updateGatewayState,
    isLoadingChangingGatewayState
  } = useGatewayStateUpdate()
  const newState = useMemo(() => (gatewayStates || []).find((state) => state.name === GatewayStateList.New), [gatewayStates])
  const lostState = useMemo(() => (gatewayStates || []).find((state) => state.name === GatewayStateList.Lost), [gatewayStates])
  const isLost = useMemo(() => gateway && gateway.stateId === lostState?.id, [gateway, lostState])

  if (!gateway) return null

  const deviceId = gateway.uniqueId

  const assignGateway = (contractorId: number | null) => {
    assignGatewayRequest.mutate({
      gatewayId: gateway.id,
      contractorId
    })
  }

  const getRightIcon = (): React.ReactNode => {
    const nodes: React.ReactNode[] = []
    if (gateway.isStagingGateway) {
      nodes.push(<SensorStagingLabel size="medium" key="staging-label" sx={{ mr: 1 }} />)
    }
    if (gateway.state?.name === GatewayStateList.Lost) {
      nodes.push(<SensorLostLabel size="medium" key="lost-label" />)
    }
    return nodes
  }

  const title = `LoRaWan Gateway: ${deviceId}`
  return (
    <>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <EntityHeader
            title={title}
            rightIcon={getRightIcon()}
            actionBlockOnTheRight={
              <GatewayStatus
                gateway={gateway}
                gatewayStates={gatewayStates || []}
                updateState={updateGatewayState}
                openDialog={openAssignGatewayDialog}
                refetchGateway={refetchGateway}
              />
            }
          >
            <Grid container spacing={2}>
              <Grid item>
                <Button variant="outlined" size="large" onClick={() => generateCode(deviceId)} sx={{ width: 210 }} disabled={loadingQRCode}>
                  {loadingQRCode ? <CircularProgress size={26} /> : 'Generate QR code'}
                </Button>
              </Grid>
              <Grid item>
                <Button variant="outlined" size="large" onClick={() => downloadLabel(deviceId)} sx={{ width: 210 }} disabled={generatingLabel}>
                  {generatingLabel ? <CircularProgress size={26} /> : 'Download label'}
                </Button>
              </Grid>
              <Grid item>
                <Button variant="outlined" size="large" onClick={isLost ? openGatewayMakeAvailableDialog : openGatewayMarkLostDialog} sx={{ width: 210 }}>
                  {isLost ? 'Make available' : 'Mark as lost'}
                </Button>
              </Grid>
            </Grid>
          </EntityHeader>
        </Grid>
        <Grid container item xs={12} flexDirection="column">
          <Grid container item alignItems="center" spacing={2} mb={1}>
            <Grid item>
              <Typography variant="h6">Gateway Information</Typography>
            </Grid>
          </Grid>
          <Grid item sx={{ width: '100%', minWidth: 0 }}>
            <GatewayInfo gateway={gateway} refetchGateway={refetchGateway} />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Current / Past Projects">
            <TabGroupFilter name={SearchParamsKeys.Tab} statusList={getStatusList({ gateway })} paramsToRemove={[SearchParamsKeys.Page, SearchParamsKeys.RowsPerPage]} />
          </CollapsibleSection>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Gateway Last Readings">
            <GatewayLastReadings gateway={gateway} />
          </CollapsibleSection>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Sensors">
            <GatewaySensors gateway={gateway} />
          </CollapsibleSection>
        </Grid>
      </Grid>
      <StatefulDialog name={DialogNames.AssignSensorToContractor} wrapContent={false}>
        <AssignSensorToContractor onSubmit={assignGateway} />
      </StatefulDialog>
      <MakeAvailableSensorModal
        sensorDeviceId={gateway.deviceId as string}
        isLoading={isLoadingChangingGatewayState}
        deviceType="gateway"
        onCancel={() => closeGatewayMakeAvailableDialog()}
        onSubmit={() => {
          updateGatewayState({
            gatewayId: gateway.id,
            state: newState as GatewayState,
            onSuccess: refetchGateway
          })
        }}
      />
      <MarkLostSensorModal
        sensorDeviceId={gateway.deviceId as string}
        isLoading={isLoadingChangingGatewayState}
        deviceType="gateway"
        onCancel={() => closeGatewayMarkLostDialog()}
        onSubmit={() => {
          updateGatewayState({
            gatewayId: gateway.id,
            state: lostState as GatewayState,
            onSuccess: refetchGateway
          })
        }}
      />
    </>
  )
}

const LoRaWanGatewayDataLoader: FC<PropsWithChildren<{ gatewayDeviceId: number }>> = ({ gatewayDeviceId, children }) => {
  const { isInitialLoading, isError, data, error, refetch } = useGatewayRequest({
    deviceId: gatewayDeviceId,
    options: {
      notifyOnChangeProps: ['isLoading', 'isError', 'isRefetching']
    }
  })
  document.title = `CLīMIT Admin Panel - LoRaWan Gateway ${data?.deviceId || ''}`
  const { data: gatewayStateData } = useGatewayStatesRequest()

  const props = {
    gateway: data || null,
    refetchGateway: refetch,
    gatewayStates: gatewayStateData || []
  }

  return (
    <DataLoad isLoading={isInitialLoading} isError={isError} errorMessage={error?.message}>
      <PropsInjector props={props}>{children}</PropsInjector>
    </DataLoad>
  )
}

const LoRaWanGatewayShow: FC = () => {
  const params = useParams()
  const { deviceId } = params as { deviceId: string }

  return (
    <HasSidebarLayout>
      <LoRaWanGatewayDataLoader gatewayDeviceId={parseInt(deviceId, 10)}>
        <LoRaWanGatewayShowContent />
      </LoRaWanGatewayDataLoader>
    </HasSidebarLayout>
  )
}

export default LoRaWanGatewayShow
